/**
 * Utilities for classifying report measures by unit and grouping them,
 * so the chart renderer can detect incommensurate measures.
 */

import { isCurrencyMeasure } from '../constants';

/** The unit category for a report measure. */
export type MeasureUnit = 'currency' | 'number';

/** A group of measure keys that share the same unit. */
export interface MeasureUnitGroup {
  unit: MeasureUnit;
  keys: string[];
}

/**
 * Classify a single measure key into a unit category.
 *
 * Returns 'currency' when the backend column type is 'currency', or when no
 * column type is available and the key matches the isCurrencyMeasure heuristic.
 * All other measures — counts, sums/averages of percentage, file_size, or
 * percent_filled — collapse to 'number' and are NOT separated from each other;
 * finer unit distinctions would require richer measure metadata from the backend.
 * This function is the single extension point if that metadata becomes available.
 */
export function measureUnit(key: string, columnType?: string): MeasureUnit {
  if (columnType === 'currency') {
    return 'currency';
  }
  if (columnType == null && isCurrencyMeasure(key)) {
    return 'currency';
  }
  return 'number';
}

/**
 * Group an ordered list of measure keys by their unit.
 *
 * Preserves first-appearance order of units and of keys within each unit.
 * Returns an empty array when measureKeys is empty.
 * Never emits an empty group.
 */
export function groupMeasuresByUnit(
  measureKeys: string[],
  columnTypes: Record<string, string>
): MeasureUnitGroup[] {
  const groups: MeasureUnitGroup[] = [];
  const unitIndex = new Map<MeasureUnit, number>();

  for (const key of measureKeys) {
    const unit = measureUnit(key, columnTypes[key]);
    const idx = unitIndex.get(unit);
    if (idx === undefined) {
      unitIndex.set(unit, groups.length);
      groups.push({ unit, keys: [key] });
    } else {
      groups[idx].keys.push(key);
    }
  }

  return groups;
}
